import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    Paper
} from '@mui/material';
import {
    FiX,
    FiRefreshCw,
    FiTrash2,
    FiPlay,
    FiPause,
    FiCloud,
    FiCloudOff,
    FiUser,
    FiPlus
} from 'react-icons/fi';
import * as azureReq from '../../../../axios/requests/azureReq';
import classes from './Microsoft365Settings.module.css';

const Microsoft365Settings = ({ open, onClose }) => {
    const [t] = useTranslation('common');
    const [loading, setLoading] = useState(false);
    const [healthStatus, setHealthStatus] = useState(null);
    const [accounts, setAccounts] = useState([]);

    const fetchHealth = useCallback(async () => {
        try {
            const res = await azureReq.checkHealth();
            const isHealthy = res.data?.outlook365Service === 'healthy' || res.status === 'success';
            setHealthStatus(isHealthy);
        } catch (e) {
            setHealthStatus(false);
        }
    }, []);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await azureReq.getAccounts();
            let accountsList = res.data?.accounts || res.data || res.accounts || res || [];

            // Ensure accountsList is an array and filter out invalid entries
            if (!Array.isArray(accountsList)) {
                accountsList = [];
            }

            // Filter and sanitize accounts to ensure all fields are valid strings
            const validAccounts = accountsList.filter(account =>
                account && typeof account === 'object' && (account.email || account.id)
            ).map(account => ({
                ...account,
                id: account.id || '',
                email: typeof account.email === 'string' ? account.email : '',
                displayName: typeof account.displayName === 'string' ? account.displayName : '',
                isActive: Boolean(account.isActive),
                lastProcessedTime: account.lastProcessedTime || null,
                lastError: typeof account.lastError === 'string' ? account.lastError : ''
            }));

            setAccounts(validAccounts);
        } catch (e) {
            console.error('Failed to fetch accounts:', e);
            setAccounts([]);
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchHealth();
            fetchAccounts();
        }
    }, [open, fetchHealth, fetchAccounts]);

    const handleConnectAccount = async () => {
        setLoading(true);
        try {
            const callbackUrl = `${window.location.origin}/auth/callback`;
            const res = await azureReq.initiateAuth(callbackUrl);
            const { authUrl } = res.data || res;

            const popup = window.open(authUrl, 'Microsoft Login', 'width=600,height=700,scrollbars=yes');

            const handleMessage = (event) => {
                if (event.origin !== window.location.origin) return;

                if (event.data?.type === 'AZURE_AUTH_SUCCESS') {
                    fetchAccounts();
                    window.removeEventListener('message', handleMessage);
                    setLoading(false);
                } else if (event.data?.type === 'AZURE_AUTH_ERROR') {
                    window.removeEventListener('message', handleMessage);
                    setLoading(false);
                }
            };

            window.addEventListener('message', handleMessage);

            const checkPopup = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(checkPopup);
                    window.removeEventListener('message', handleMessage);
                    setLoading(false);
                }
            }, 500);
        } catch (e) {
            console.error('Failed to initiate authentication:', e);
            setLoading(false);
        }
    };

    const handleDeleteAccount = async (accountId) => {
        try {
            await azureReq.deleteAccount(accountId);
            fetchAccounts();
        } catch (e) {
            console.error('Failed to disconnect account:', e);
        }
    };

    const handleToggleAccount = async (account) => {
        try {
            if (account.isActive) {
                await azureReq.deactivateAccount(account.id);
            } else {
                await azureReq.activateAccount(account.id);
            }
            fetchAccounts();
        } catch (e) {
            console.error('Failed to update account status:', e);
        }
    };

    const handlePollAccount = async (accountId) => {
        try {
            await azureReq.pollAccount(accountId);
        } catch (e) {
            console.error('Failed to trigger email poll:', e);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className={classes.dialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <FiX />
                        </IconButton>
                        <Chip
                            icon={healthStatus ? <FiCloud /> : <FiCloudOff />}
                            label={healthStatus ? t('Service Online') : t('Service Offline')}
                            color={healthStatus ? 'success' : 'error'}
                            size="small"
                        />
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">{t('Microsoft 365 Settings')}</Typography>
                        <img
                            src="https://img.icons8.com/fluency/48/microsoft-365.png"
                            alt="Microsoft 365"
                            style={{ width: 32, height: 32 }}
                        />
                    </Box>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent className={classes.dialogContent}>
                <Box className={classes.accountsSection}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {t('Connected Accounts')}
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Tooltip title={t('Refresh')}>
                                <IconButton onClick={fetchAccounts} size="small">
                                    <FiRefreshCw />
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FiPlus />}
                                onClick={handleConnectAccount}
                                disabled={loading || !healthStatus}
                                size="small"
                            >
                                {t('Connect Account')}
                            </Button>
                        </Box>
                    </Box>

                    {!healthStatus && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('Microsoft 365 service is currently offline. Please try again later.')}
                        </Alert>
                    )}

                    {accounts.length === 0 ? (
                        <Paper className={classes.emptyState}>
                            <FiUser style={{ fontSize: 48, color: '#9e9e9e' }} />
                            <Typography color="text.secondary">
                                {t('No accounts connected yet')}
                            </Typography>
                        </Paper>
                    ) : (
                        <List className={classes.accountList}>
                            {accounts.map((account) => (
                                <ListItem key={account.id} className={classes.accountItem}>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight="medium">
                                                    {account.displayName || account.email || t('Unknown Account')}
                                                </Typography>
                                                <Chip
                                                    label={account.isActive ? t('Active') : t('Inactive')}
                                                    color={account.isActive ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box component="span">
                                                {account.email && (
                                                    <Typography variant="body2" color="text.secondary" component="span" display="block">
                                                        {account.email}
                                                    </Typography>
                                                )}
                                                {account.lastProcessedTime && (
                                                    <Typography variant="caption" color="text.secondary" component="span" display="block">
                                                        {t('Last sync')}: {new Date(account.lastProcessedTime).toLocaleString()}
                                                    </Typography>
                                                )}
                                                {account.lastError && (
                                                    <Typography variant="caption" color="error" component="span" display="block">
                                                        {t('Error')}: {account.lastError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title={t('Sync now')}>
                                            <IconButton
                                                onClick={() => handlePollAccount(account.id)}
                                                disabled={!account.isActive}
                                                size="small"
                                            >
                                                <FiRefreshCw />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={account.isActive ? t('Deactivate') : t('Activate')}>
                                            <IconButton
                                                onClick={() => handleToggleAccount(account)}
                                                size="small"
                                            >
                                                {account.isActive ? <FiPause /> : <FiPlay />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('Disconnect')}>
                                            <IconButton
                                                onClick={() => handleDeleteAccount(account.id)}
                                                color="error"
                                                size="small"
                                            >
                                                <FiTrash2 />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions className={classes.dialogActions}>
                <Button onClick={onClose} color="inherit">
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Microsoft365Settings;
